import { ClickAwayListener, Menu, MenuItem } from '@mui/material';
import React, { useMemo, useRef, useState } from 'react';
import IconButton from 'ui/elements/icons/IconButton';
import MentionIcon from '../icons/MentionIcon';
import Card from 'ui/views/cards/Card';
import Avatar from 'ui/elements/avatars/Avatar';
import TextField from '../form/TextField';
import { Mention } from '../form/RichTextEditor';
import debounce from 'lodash.debounce';
import 'styles/global/overrides/quill-editor-overrides.scss';

interface Props {
  onSelect: (mention: Mention) => void;
  style?: React.CSSProperties;
  mentionSearchFunction: (query: string) => Promise<Mention[]>;
}

export default function MentionSelect({ onSelect, mentionSearchFunction, style }: Props) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);
  const [searchQuery, setSearchQuery] = useState('');
  const [mentionResults, setMentionResults] = useState<Mention[]>([]);
  const firstResultRef = useRef<HTMLLIElement>(null);
  const textFieldRef = useRef<HTMLInputElement>();

  const handleMentionFunc = async function (searchTerm: any) {
    const result = await mentionSearchFunction(searchTerm);
    setMentionResults(result);
  };

  const debouncedMentionFunc = useMemo(() => {
    return debounce(handleMentionFunc, 300);
  }, [mentionSearchFunction]);

  function handleClose() {
    setAnchorEl(null);
  }

  function handleClick(e: React.MouseEvent<HTMLElement>) {
    setAnchorEl(anchorEl ? null : e.currentTarget);
    setTimeout(() => {
      textFieldRef.current?.focus();
    }, 100);
  }

  function handleClearSearch() {
    setSearchQuery('');
    setMentionResults([]);
  }

  return (
    <div style={style}>
      <IconButton size="small" color="indigo" onClick={handleClick}>
        <MentionIcon />
      </IconButton>

      <Menu
        disableAutoFocusItem // needed to make the MUI menu keyboard navigation work
        disableAutoFocus
        MenuListProps={{ style: { minWidth: '240px' } }}
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <ClickAwayListener onClickAway={handleClearSearch}>
          <span>
            <TextField
              inputRef={textFieldRef}
              onChange={e => {
                debouncedMentionFunc(e.target.value);
                setSearchQuery(e.target.value);
              }}
              value={searchQuery}
              onKeyUp={e => {
                if (e.key === 'ArrowDown') {
                  firstResultRef.current?.focus();
                }
              }}
              autoFocus
              autoComplete="off"
              placeholder="Find people & companies to tag"
              sx={{ '.MuiInputBase-root': { border: 'none' } }}
            />

            {searchQuery && (
              <Card padding="none" elevation={2}>
                {mentionResults.length > 0 && (
                  <>
                    {mentionResults.length > 0 && <div className="ql-mention-divider">in your network</div>}
                    {mentionResults.map((result, i) => {
                      return (
                        <MenuItem
                          ref={i === 0 ? firstResultRef : null}
                          key={`search-result-${i}`}
                          onClick={() => {
                            onSelect({
                              id: result.id,
                              value: result.value,
                              type: result.type,
                            });
                            handleClose();
                            handleClearSearch();
                          }}
                        >
                          <div className="u-flex u-flex-align-center">
                            <Avatar
                              className="u-half-spacing-right"
                              size={36}
                              userName={result.value}
                              imageUrl={result.imageURL}
                            />
                            <div className="u-flex u-flex--column">
                              <span className="u-ellipsis text-large text-weight-medium">{result.value}</span>
                              <span className="u-ellipsis text-metadata">
                                {result.type === 'user' ? 'Person' : 'Company'}
                              </span>
                            </div>
                          </div>
                        </MenuItem>
                      );
                    })}
                  </>
                )}
              </Card>
            )}
          </span>
        </ClickAwayListener>
      </Menu>
    </div>
  );
}
